import { useId, useState, type ChangeEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  ErrorText,
  HelpTip,
  LinkExistingDocumentAction,
  NotApplicableAttestDialog,
  UnlinkDocumentDialog,
  cn,
  errorMessage,
  isAccountPlaceholderItem,
  isStatePlaceholderItem,
  placeholderAccountsLine,
  readableCategoryName,
} from '@dataroom/shared';
import type { DdPhaseSummary, DdRequest, RoomDocument } from '@dataroom/shared';
import {
  useDdChecklist,
  useDdSummary,
  useFinalizeIngest,
  useLinkDdDocument,
  useSetDdNotApplicable,
  useUnlinkDdDocument,
} from '@/api/hooks';
import { requestState, type RequestState } from '@/lib/checklist';
import { contentTypeOf, fileToBase64, oversizeMessage } from '@/lib/file';
import { DdAccountsDialog } from './DdAccountsPanel';
import { SellerDocStatus } from './SellerDocStatus';
import { ReadableCopyButton } from './UnreadableFiles';

/** Items fetched per phase on the home checklist (the list endpoint's max). */
const HOME_PAGE_SIZE = 200;

/**
 * Items SHOWN per phase on the home page at first (missing first), and per
 * "Show more". The home page sits between the upload box and the submitted
 * documents: a 300-item phase rendered in full pushed the documents ~18,000px
 * down. The full checklist is one click away.
 */
export const HOME_VISIBLE_ITEMS = 10;

/** Missing first (what the seller can act on), then waiting, then resolved. */
const ORDER: Record<RequestState, number> = {
  missing: 0,
  waiting_on_review: 1,
  covered: 2,
  not_applicable: 3,
};

/**
 * The seller home's checklist items, below the dashboard cards (the progress card
 * holds the counts): what the intermediary
 * has asked for and where each item stands — covered, missing, waiting on review
 * or doesn't apply — so the seller sees at a glance what is left. Every item
 * counts, required or optional (ADR-0032): each is resolved only by a document or
 * the seller's attested "doesn't apply to my company", which a missing item
 * offers right here. The seller does not
 * match files to items: uploads land in the library and the matching happens
 * on the server; each item shows the status the API returns. Submitting a
 * single document for one item stays available inside that item, as the
 * fallback, and so does linking a document already in the data room (and
 * unlinking one). The full checklist (linking, "does not apply", certification,
 * phase questions) is one click away on the Checklist tab.
 */
export function RequestChecklist({
  roomId,
  documents,
}: {
  roomId: string;
  /** The seller's uploads (the room documents listing) — names and filing status. */
  documents: RoomDocument[];
}) {
  const summary = useDdSummary(roomId);
  const phases = summary.data ?? [];
  const docsById = new Map(documents.map((d) => [d.id, d] as const));
  const firstOpen = phases.find((p) => p.unresolved > 0)?.phaseId ?? phases[0]?.phaseId ?? null;
  // Nothing requested yet: the dashboard's progress card says so.
  if (summary.isSuccess && phases.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle>Checklist</CardTitle>
          <Link
            to={`/rooms/${roomId}/checklist`}
            className="text-sm font-medium text-primary hover:underline"
          >
            Open the full checklist
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          What the deal team has asked for. Items update as your files are matched to them.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {summary.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : summary.error ? (
          <ErrorText error={summary.error} />
        ) : (
          phases.map((phase) => (
            <PhaseItems
              key={phase.phaseId ?? 'ungrouped'}
              roomId={roomId}
              phase={phase}
              docsById={docsById}
              defaultOpen={phase.phaseId === firstOpen}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

/** "2 missing · 1 waiting", or "All resolved" — the server's counts. */
function phaseStatusLine(phase: DdPhaseSummary): string {
  if (phase.unresolved === 0) return 'All resolved';
  const waiting = phase.waitingOnReview > 0 ? ` · ${phase.waitingOnReview} waiting` : '';
  return `${phase.missing} missing${waiting}`;
}

/** One phase: a native disclosure (keyboard + screen-reader friendly) listing
 * its items, missing first. */
function PhaseItems({
  roomId,
  phase,
  docsById,
  defaultOpen,
}: {
  roomId: string;
  phase: DdPhaseSummary;
  docsById: ReadonlyMap<string, RoomDocument>;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="rounded-md border"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <span className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden="true"
            className={cn('text-ink-3 transition-transform', open && 'rotate-90')}
          >
            ▸
          </span>
          <span className="truncate">{phase.name ?? 'Other requested items'}</span>
        </span>
        <span className="shrink-0 text-xs font-medium text-ink-2">
          {phaseStatusLine(phase)} · {phase.total} items
        </span>
      </summary>
      {open ? <PhaseItemList roomId={roomId} phaseId={phase.phaseId} docsById={docsById} /> : null}
    </details>
  );
}

function PhaseItemList({
  roomId,
  phaseId,
  docsById,
}: {
  roomId: string;
  phaseId: string | null;
  docsById: ReadonlyMap<string, RoomDocument>;
}) {
  const list = useDdChecklist(roomId, {
    phaseId: phaseId ?? undefined,
    limit: HOME_PAGE_SIZE,
    offset: 0,
  });
  const [visible, setVisible] = useState(HOME_VISIBLE_ITEMS);
  if (list.isPending) return <p className="px-3 pb-3 text-sm text-muted-foreground">Loading…</p>;
  if (list.error) return <ErrorText error={list.error} className="px-3 pb-3" />;
  const requests = [...(list.data?.requests ?? [])].sort(
    (a, b) => ORDER[requestState(a)] - ORDER[requestState(b)],
  );
  const total = list.data?.total ?? requests.length;
  const shown = requests.slice(0, visible);
  // The "?" on "Doesn't apply to my company" sits on the first row that offers it
  // in each phase (a missing item), not on every row.
  const firstMissingId = shown.find((r) => requestState(r) === 'missing')?.id;
  return (
    <div className="border-t">
      <ul className="divide-y">
        {shown.map((req) => (
          <RequestItem
            key={req.id}
            roomId={roomId}
            req={req}
            docsById={docsById}
            naTip={req.id === firstMissingId}
          />
        ))}
      </ul>
      {total > shown.length ? (
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-3 py-2 text-xs text-muted-foreground">
          <span>
            Showing {shown.length} of {total}.
          </span>
          {requests.length > shown.length ? (
            <button
              type="button"
              className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setVisible((n) => n + HOME_VISIBLE_ITEMS)}
            >
              Show {Math.min(HOME_VISIBLE_ITEMS, requests.length - shown.length)} more
            </button>
          ) : null}
          <Link
            to={`/rooms/${roomId}/checklist`}
            className="font-medium text-primary hover:underline"
          >
            See every item
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function StateChip({ state }: { state: RequestState }) {
  switch (state) {
    case 'covered':
      return (
        <Chip tone="filed" icon="check">
          Covered
        </Chip>
      );
    case 'not_applicable':
      return (
        <Chip tone="na" icon="notApplicable">
          Doesn’t apply
        </Chip>
      );
    case 'waiting_on_review':
      return (
        <Chip tone="processing" icon="eye">
          Waiting on review
        </Chip>
      );
    default:
      return (
        <Chip tone="check" icon="alert">
          Missing
        </Chip>
      );
  }
}

export function RequestItem({
  roomId,
  req,
  docsById,
  note,
  naTip = false,
}: {
  roomId: string;
  req: DdRequest;
  docsById: ReadonlyMap<string, RoomDocument>;
  /** A line under the title (the dashboard's due date). */
  note?: ReactNode;
  /** Show the "?" beside "Doesn't apply to my company" (the first such row of a phase). */
  naTip?: boolean;
}) {
  const state = requestState(req);
  const path = Array.isArray(req.categoryPath) ? req.categoryPath : [];
  // Who linked each document: the AI ("Matched automatically") or a person.
  const auto = new Set([
    ...(req.links ?? []).filter((l) => l.source === 'ai').map((l) => l.documentId),
    ...(req.autoLinkedDocumentIds ?? []),
  ]);
  const suggested = (req.reviewDocumentIds ?? []).filter(
    (id) => !req.linkedDocumentIds.includes(id),
  );
  // "One category per account" (ADR-0037): answered by listing the accounts.
  const placeholder = isAccountPlaceholderItem(req) || isStatePlaceholderItem(req);
  const link = useLinkDdDocument(roomId);
  const unlink = useUnlinkDdDocument(roomId);
  // The linked document whose unlink is being confirmed.
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const docName = (id: string) => docsById.get(id)?.name ?? 'A document in the data room';
  /** Link the chosen documents one by one (the API takes one per call). */
  async function linkDocuments(documentIds: string[]) {
    for (const documentId of documentIds) {
      await link.mutateAsync({ requestId: req.id, documentId });
    }
  }
  return (
    <li className="space-y-1.5 px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{req.title}</p>
          {path.length > 0 ? (
            <p className="truncate text-xs text-muted-foreground">
              {path.map(readableCategoryName).join(' › ')}
            </p>
          ) : null}
          {note}
          {placeholder ? (
            <p className="text-xs text-ink-2">{placeholderAccountsLine(req)}</p>
          ) : null}
          {!req.required && state === 'missing' ? (
            <p className="text-xs text-muted-foreground">
              Optional — still needs a document, or confirm it doesn’t apply
            </p>
          ) : null}
        </div>
        <StateChip state={state} />
      </div>
      {req.linkedDocumentIds.length > 0 ? (
        <ul className="space-y-1" aria-label={`Documents for ${req.title}`}>
          {req.linkedDocumentIds.map((id) => {
            const doc = docsById.get(id);
            return (
              <li key={id} className="space-y-1 text-xs">
                <p className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">
                    {doc?.name ?? 'A document in the data room'}
                    {auto.has(id) ? (
                      <span className="text-muted-foreground"> · Matched automatically</span>
                    ) : null}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    {doc ? (
                      <SellerDocStatus status={doc.status} unreadable={doc.unreadable} />
                    ) : null}
                    <button
                      type="button"
                      className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Unlink “${docName(id)}”`}
                      onClick={() => {
                        unlink.reset();
                        setUnlinking(id);
                      }}
                    >
                      Unlink
                    </button>
                  </span>
                </p>
                {/* We could not read it, so it does not answer the item yet. */}
                {doc?.unreadable ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p className="text-ink-2">{doc.unreadable.message}</p>
                    <ReadableCopyButton roomId={roomId} document={doc} compact />
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {suggested.length > 0 ? (
        <ul className="space-y-1" aria-label={`Suggested for ${req.title}`}>
          {suggested.map((id) => (
            <li key={id} className="truncate text-xs text-muted-foreground">
              {docsById.get(id)?.name ?? 'A document in the data room'} · Matched by the AI, waiting
              on review
            </li>
          ))}
        </ul>
      ) : null}
      {state === 'missing' ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {placeholder ? (
            <ListAccountsAction roomId={roomId} req={req} />
          ) : (
            <>
              <LinkExistingDocumentAction
                item={req}
                documents={[...docsById.values()]}
                onLink={linkDocuments}
                appearance="link"
              />
              <SingleDocumentFallback roomId={roomId} req={req} />
            </>
          )}
          <DoesNotApplyAction roomId={roomId} req={req} tip={naTip} />
        </div>
      ) : !placeholder && state !== 'not_applicable' ? (
        <div>
          <LinkExistingDocumentAction
            item={req}
            documents={[...docsById.values()]}
            onLink={linkDocuments}
            appearance="link"
          />
        </div>
      ) : null}
      {unlinking ? (
        <UnlinkDocumentDialog
          itemTitle={req.title}
          documentName={docName(unlinking)}
          pending={unlink.isPending}
          error={<ErrorText error={unlink.error} />}
          onCancel={() => setUnlinking(null)}
          onConfirm={() =>
            unlink.mutate(
              { requestId: req.id, documentId: unlinking },
              { onSuccess: () => setUnlinking(null) },
            )
          }
        />
      ) : null}
    </li>
  );
}

/** "List your accounts": the account list for a "one category per account" item. */
function ListAccountsAction({ roomId, req }: { roomId: string; req: DdRequest }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setOpen(true)}
      >
        List your accounts
      </button>
      {open ? (
        <DdAccountsDialog
          roomId={roomId}
          placeholderTagId={req.categoryTagId}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/** "Doesn't apply to my company": opens the attestation; only an explicit,
 * ticked confirmation marks the item (ADR-0032). */
function DoesNotApplyAction({
  roomId,
  req,
  tip,
}: {
  roomId: string;
  req: DdRequest;
  tip: boolean;
}) {
  const na = useSetDdNotApplicable(roomId);
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="inline-flex items-center gap-1">
        <button
          type="button"
          className="rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => {
            na.reset();
            setOpen(true);
          }}
        >
          Doesn’t apply to my company
        </button>
        {tip ? <HelpTip id="seller.dashboard.does-not-apply" /> : null}
      </div>
      {open ? (
        <NotApplicableAttestDialog
          itemTitle={req.title}
          pending={na.isPending}
          error={<ErrorText error={na.error} />}
          onCancel={() => setOpen(false)}
          onConfirm={() =>
            na.mutate(
              { requestId: req.id, notApplicable: true, attest: true },
              { onSuccess: () => setOpen(false) },
            )
          }
        />
      ) : null}
    </>
  );
}

/** The fallback: send one file for exactly this item (upload, then link it). */
function SingleDocumentFallback({ roomId, req }: { roomId: string; req: DdRequest }) {
  const finalize = useFinalizeIngest(roomId);
  const link = useLinkDdDocument(roomId);
  const inputId = useId();
  const [state, setState] = useState<'idle' | 'sending' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  async function send(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      setState('error');
      setMessage(tooBig);
      return;
    }
    setState('sending');
    setMessage(null);
    try {
      const res = await finalize.mutateAsync({
        filename: file.name,
        contentType: contentTypeOf(file),
        contentBase64: await fileToBase64(file),
      });
      if (res.documentId === undefined) {
        // Already in the room as a document this person cannot see (ADR-0061):
        // the API says only that, so there is nothing here to link.
        setState('idle');
        setMessage('That file is already in this room.');
        return;
      }
      await link.mutateAsync({ requestId: req.id, documentId: res.documentId });
      setState('idle');
      // Identical bytes were already in the room (ADR-0028): nothing was
      // uploaded, and the existing document is the one linked to this item.
      if (res.skipped) setMessage('That file was already in this room — it is now linked here.');
    } catch (err) {
      setState('error');
      setMessage(errorMessage(err) ?? 'That upload didn’t go through. Try again.');
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label
        htmlFor={inputId}
        className={cn(
          'cursor-pointer rounded text-xs font-medium text-primary hover:underline focus-within:ring-2 focus-within:ring-ring',
          state === 'sending' && 'pointer-events-none text-muted-foreground',
        )}
      >
        {state === 'sending' ? 'Sending…' : 'Submit a single document for this item'}
        <input
          id={inputId}
          type="file"
          className="sr-only"
          disabled={state === 'sending'}
          aria-label={`Submit a single document for ${req.title}`}
          onChange={(e) => void send(e)}
        />
      </label>
      {state === 'error' && message ? (
        <span className="text-xs text-destructive" role="alert">
          {message}
        </span>
      ) : null}
      {state === 'idle' && message ? (
        <span className="text-xs text-muted-foreground" role="status">
          {message}
        </span>
      ) : null}
    </div>
  );
}
